// Authentication service using real API
import logger from '../utils/logger'

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  is_active: boolean;
  created_at: string;
  school_id?: string;
  district?: string;
  rd_block?: string;
}

interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    user: User;
    token: string;
  };
}

import router from '../router'
import { useAuthStore } from '../stores/auth'

class AuthService {
  private isAuthenticated = false;
  private currentUser: User | null = null;
  private token: string | null = null;

  constructor() {
    // Initialize from localStorage on startup
    this.initializeFromStorage();
  }

  private initializeFromStorage(): void {
    try {
      // SECURITY: Access tokens are now in HTTP-only cookies (not accessible to JavaScript)
      // Only restore user info and authentication state, not tokens
      const stored = localStorage.getItem('isAuthenticated');
      const userStr = localStorage.getItem('currentUser');
      
      if (stored === 'true' && userStr) {
        this.isAuthenticated = true;
        this.currentUser = JSON.parse(userStr);
        this.token = null; // Token is in HTTP-only cookie, not accessible
      }
    } catch (error) {
      logger.warn('AuthService: Failed to initialize from localStorage:', error);
    }
  }

  async login(email: string, password: string): Promise<{ success: boolean; user?: User }> {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // SECURITY: Required for HTTP-only cookies
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '')
        logger.error('AuthService.login: HTTP error', response.status, errorText)
        return { success: false }
      }

      const contentType = response.headers.get('content-type') || ''
      if (!contentType.toLowerCase().includes('application/json')) {
        const errorText = await response.text().catch(() => '')
        logger.error('AuthService.login: Expected JSON response but received:', contentType, errorText)
        return { success: false }
      }

      const data: LoginResponse = await response.json();

      if (data.success && data.data.user) {
        this.isAuthenticated = true;
        this.currentUser = data.data.user;
        // SECURITY: Access token is now in HTTP-only cookie (not accessible to JavaScript)
        this.token = null; // Don't store token - it's in HTTP-only cookie
        const csrf = (data as any).data?.csrf || ''
        
        // SECURITY: Clean up any old tokens from localStorage (from before cookie migration)
        localStorage.removeItem('token')
        localStorage.removeItem('authToken')
        localStorage.removeItem('teacherToken')
        
        // Store in localStorage for persistence (user info only, not tokens)
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
        // Don't store token in localStorage - it's in HTTP-only cookie
        if (csrf) localStorage.setItem('csrf', csrf);
        
        return { success: true, user: this.currentUser };
      }
      return { success: false };
    } catch (error) {
      logger.error('Login error:', error);
      return { success: false };
    }
  }

  async logout(): Promise<void> {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    } catch {}
    this.isAuthenticated = false;
    this.currentUser = null;
    this.token = null;
    
    // SECURITY: Clear all authentication data from localStorage
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('csrf');
    // SECURITY: Clean up any old tokens (from before cookie migration)
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('teacherToken');

    // Sync Pinia store if present and redirect
    try {
      const store = useAuthStore()
      store.isAuthenticated = false
      store.currentUser = null
      store.token = null
      store.csrf = null
    } catch {}

    try {
      router.push('/login')
    } catch {}
  }

  getAuthStatus(): boolean {
    // SECURITY: Access tokens are now in HTTP-only cookies (not accessible to JavaScript)
    // Check authentication status from localStorage flag only
    const stored = localStorage.getItem('isAuthenticated');
    const userStr = localStorage.getItem('currentUser');
    
    if (stored === 'true' && userStr) {
      this.isAuthenticated = true;
      this.currentUser = JSON.parse(userStr);
      this.token = null; // Token is in HTTP-only cookie, not accessible
    }
    return this.isAuthenticated;
  }

  getToken(): string | null {
    // SECURITY: Access tokens are now in HTTP-only cookies (not accessible to JavaScript)
    // This method returns null - tokens are automatically sent via cookies
    return null;
  }

  getAuthHeaders(): Record<string, string> {
    // SECURITY: Access tokens are now in HTTP-only cookies (not accessible to JavaScript)
    // No need to return Authorization header - cookies are sent automatically with credentials: 'include'
    return {};
  }

  getCurrentUser(): User | null {
    // First check if we have a current user in this service
    if (this.currentUser) {
      return this.currentUser;
    }
    
    // If not, try to refresh from localStorage
    this.refreshFromStorage();
    
    if (this.currentUser) {
      return this.currentUser;
    }
    
    logger.debug('AuthService: No user found');
    return null;
  }

  private refreshFromStorage(): void {
    try {
      // SECURITY: Access tokens are now in HTTP-only cookies (not accessible to JavaScript)
      const storedUser = localStorage.getItem('currentUser');
      const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
      
      if (storedUser && isAuthenticated) {
        this.currentUser = JSON.parse(storedUser);
        this.token = null; // Token is in HTTP-only cookie, not accessible
        this.isAuthenticated = true;
      }
    } catch (error) {
      logger.warn('AuthService: Failed to refresh from localStorage:', error);
    }
  }

  isLoggedIn(): boolean {
    return this.getAuthStatus();
  }

  async requestPasswordReset(email: string): Promise<{
    success: boolean
    message?: string
    devResetUrl?: string
  }> {
    try {
      const resp = await fetch('/api/auth/request-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim() }),
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        return {
          success: false,
          message: data?.message || `Request failed (${resp.status})`,
        }
      }
      return {
        success: !!data?.success,
        message: data?.message,
        devResetUrl: data?.devResetUrl,
      }
    } catch {
      return { success: false, message: 'Network error. Please try again.' }
    }
  }

  async resetPassword(token: string, password: string): Promise<boolean> {
    try {
      const resp = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, password }),
      })
      
      if (!resp.ok) {
        let errorData: any = {}
        try {
          const errorText = await resp.text()
          errorData = JSON.parse(errorText)
        } catch {
          // Could not parse error response
        }
        
        // Provide user-friendly error messages
        if (errorData?.error === 'jwt expired' || errorData?.message?.toLowerCase().includes('expired')) {
          throw new Error('Reset token has expired. Please request a new password reset link.')
        } else if (errorData?.message) {
          throw new Error(errorData.message)
        } else {
          throw new Error(`Server error: ${resp.status} ${resp.statusText}`)
        }
      }
      
      const data = await resp.json()
      
      if (!data?.success) {
        const errorMsg = data?.message || 'Password reset failed'
        
        // Check for expired token in message
        if (errorMsg.toLowerCase().includes('expired')) {
          throw new Error('Reset token has expired. Please request a new password reset link.')
        }
        
        throw new Error(errorMsg)
      }
      
      return true
    } catch (e) {
      logger.error('ResetPassword error:', e)
      if (e instanceof Error) {
        throw e // Re-throw to let component handle it
      }
      throw new Error('Failed to reset password. Please try again.')
    }
  }
}

export const authService = new AuthService();
